import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountingDataTable from '../../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../../accounting/shared/AccountingDataTable';
import BillingService, {
  DealerDueItem,
  DealerNoticeStatus,
  DealerRenewalNotice,
} from '../../../services/BillingService';
import SuperAdminService, { Company, DealerRow } from '../../../services/SuperAdminService';
import GuideButton from '../../common/guide/GuideButton';

const STATUS_META: Record<DealerNoticeStatus, { label: string; color: 'warning' | 'success' | 'default' }> = {
  Active: { label: 'Açık', color: 'warning' },
  Closed: { label: 'Kapatıldı', color: 'success' },
  Cancelled: { label: 'İptal', color: 'default' },
};

const dateOnly = (iso?: string | null) => (iso || '').slice(0, 10);

const fmtDate = (iso?: string | null) => {
  const s = dateOnly(iso);
  if (s.length < 10) return '—';
  const [y, m, d] = s.split('-');
  return `${d}.${m}.${y}`;
};

const daysLabel = (days: number) => {
  if (days < 0) return `${Math.abs(days)} gün geçti`;
  if (days === 0) return 'Bugün';
  return `${days} gün`;
};

const DealerNoticesTab: React.FC = () => {
  const [rows, setRows] = React.useState<DealerRenewalNotice[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);
  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<DealerRenewalNotice | null>(null);
  const [confirm, setConfirm] = React.useState<{ action: 'close' | 'cancel'; row: DealerRenewalNotice } | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setRows(await BillingService.getDealerNotices(statusFilter || undefined));
    } catch (e: any) {
      setError(e?.message || 'Bayi bildirimleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  React.useEffect(() => {
    load();
  }, [load]);

  const runConfirm = async () => {
    if (!confirm) return;
    try {
      if (confirm.action === 'close') await BillingService.closeDealerNotice(confirm.row.id);
      else await BillingService.cancelDealerNotice(confirm.row.id);
      setToast(confirm.action === 'close' ? 'Bildirim kapatıldı.' : 'Bildirim iptal edildi.');
      setConfirm(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'İşlem tamamlanamadı.');
    }
  };

  const columns: DataTableColumn<DealerRenewalNotice>[] = [
    { id: 'dealer', label: 'Bayi', minWidth: '140px', render: (r) => r.dealerName || '—' },
    {
      id: 'company',
      label: 'Şirket / şube',
      minWidth: '180px',
      render: (r) => (
        <Box>
          <Typography variant="body2">{r.companyName || r.companyId}</Typography>
          <Typography variant="caption" color="text.secondary">
            {r.branchName || 'Şirket geneli'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'title',
      label: 'Kalem',
      minWidth: '160px',
      render: (r) => (
        <Box>
          <Typography variant="body2">{r.title}</Typography>
          {r.detail ? (
            <Typography variant="caption" color="text.secondary">{r.detail}</Typography>
          ) : null}
        </Box>
      ),
    },
    { id: 'expiry', label: 'Bitiş', width: '110px', render: (r) => fmtDate(r.expiryDate) },
    { id: 'days', label: 'Kalan', width: '110px', render: (r) => daysLabel(r.daysRemaining) },
    { id: 'email', label: 'E-posta', minWidth: '180px', field: 'notifyEmail' },
    {
      id: 'sent',
      label: 'Giden hatırlatma',
      width: '160px',
      render: (r) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {[30, 15, 7].map((b) => (
            <Chip
              key={b}
              size="small"
              label={b}
              color={r.sentBuckets?.includes(b) ? 'success' : 'default'}
              variant={r.sentBuckets?.includes(b) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'Durum',
      width: '110px',
      render: (r) => {
        const meta = STATUS_META[r.status] || STATUS_META.Active;
        return <Chip size="small" label={meta.label} color={meta.color} />;
      },
    },
    {
      id: 'actions',
      label: '',
      width: '90px',
      align: 'right',
      render: (r) => r.status !== 'Active' ? null : (
        <Box onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Kapat">
            <IconButton size="small" onClick={() => setConfirm({ action: 'close', row: r })}>
              <CheckCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="İptal">
            <IconButton size="small" onClick={() => setConfirm({ action: 'cancel', row: r })}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          size="small"
          label="Durum"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ width: { xs: '100%', sm: 180 } }}
        >
          <MenuItem value="">Tümü</MenuItem>
          <MenuItem value="Active">Açık</MenuItem>
          <MenuItem value="Closed">Kapatılan</MenuItem>
          <MenuItem value="Cancelled">İptal</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Yeni Bildirim
        </Button>
        <GuideButton guideKey="superadmin.dealerNotices" />
      </Paper>

      <AccountingDataTable<DealerRenewalNotice>
        columns={columns}
        rows={rows}
        getRowId={(r) => String(r.id)}
        loading={loading}
        ariaLabel="Bayi bildirimleri"
        emptyMessage="Henüz bayi bildirimi yok."
        defaultRowsPerPage={25}
        rowsPerPageOptions={[25, 50, 100]}
        onRowClick={(r) => {
          if (r.status !== 'Active') return;
          setEditing(r);
          setFormOpen(true);
        }}
        clickable
      />

      {formOpen && (
        <NoticeForm
          editing={editing}
          onClose={() => setFormOpen(false)}
          onSaved={async (message) => {
            setFormOpen(false);
            setToast(message);
            await load();
          }}
          onError={setError}
        />
      )}

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm?.action === 'close' ? 'Bildirimi kapat' : 'Bildirimi iptal et'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm?.action === 'close'
              ? 'Bu kayıt kapanır ve sonraki 30, 15, 7 gün hatırlatmaları gitmez.'
              : 'Bu kayıt iptal edilir ve sonraki hatırlatmalar gitmez.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Vazgeç</Button>
          <Button variant="contained" color={confirm?.action === 'cancel' ? 'error' : 'primary'} onClick={runConfirm}>
            Onayla
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} message={toast || ''} />
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </>
  );
};

interface NoticeFormProps {
  editing: DealerRenewalNotice | null;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

const NoticeForm: React.FC<NoticeFormProps> = ({ editing, onClose, onSaved, onError }) => {
  const [dealers, setDealers] = React.useState<DealerRow[]>([]);
  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [dealer, setDealer] = React.useState<DealerRow | null>(null);
  const [company, setCompany] = React.useState<Company | null>(null);
  const [dueItems, setDueItems] = React.useState<DealerDueItem[]>([]);
  const [dueLoading, setDueLoading] = React.useState(false);
  const [dueError, setDueError] = React.useState<string | null>(null);
  const [picked, setPicked] = React.useState<string[]>([]);
  const [sourceKey, setSourceKey] = React.useState(editing?.sourceKey || '');
  const [title, setTitle] = React.useState(editing?.title || '');
  const [detail, setDetail] = React.useState(editing?.detail || '');
  const [branchId, setBranchId] = React.useState(editing?.branchId || '');
  const [branchName, setBranchName] = React.useState(editing?.branchName || '');
  const [expiry, setExpiry] = React.useState(dateOnly(editing?.expiryDate));
  const [email, setEmail] = React.useState(editing?.notifyEmail || '');
  const [phone, setPhone] = React.useState(editing?.notifyPhone || '');
  const [notes, setNotes] = React.useState(editing?.notes || '');
  const [saving, setSaving] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const svc = SuperAdminService.getInstance();
        const [dealerList, companyList] = await Promise.all([
          svc.getDealers(),
          svc.getAllCompanies(),
        ]);
        if (cancelled) return;
        setDealers(dealerList.filter((d) => d.isActive));
        setCompanies(companyList);
        if (editing) {
          setDealer(dealerList.find((d) => d.id === editing.dealerId) || null);
          setCompany(companyList.find((c) => c.id === editing.companyId) || null);
        }
      } catch (e: any) {
        if (!cancelled) setFormError(e?.message || 'Listeler yüklenemedi.');
      }
    })();
    return () => { cancelled = true; };
  }, [editing]);

  const dealerCompanies = companies.filter((c) => dealer && c.dealerId === dealer.id);

  React.useEffect(() => {
    if (!dealer || !company || editing) {
      setDueItems([]);
      return;
    }
    let cancelled = false;
    setDueLoading(true);
    setDueError(null);
    BillingService.getDealerDueItems(dealer.id, company.id)
      .then((items) => { if (!cancelled) setDueItems(items); })
      .catch((e: any) => {
        if (cancelled) return;
        setDueItems([]);
        setDueError(e?.message || 'Kalemler yüklenemedi.');
      })
      .finally(() => { if (!cancelled) setDueLoading(false); });
    return () => { cancelled = true; };
  }, [dealer, company, editing]);

  const openItems = dueItems.filter((item) => !item.openNoticeId);
  const toggleItem = (key: string) => {
    setPicked((current) => current.includes(key) ? current.filter((k) => k !== key) : [...current, key]);
  };

  const save = async () => {
    if (!dealer || !company) {
      setFormError('Bayi ve şirket seçin.');
      return;
    }
    if (!email.trim()) {
      setFormError('Bayi e-postası zorunludur.');
      return;
    }
    if (editing) {
      if (!title.trim() || !expiry) {
        setFormError('Kalem ve bitiş tarihi zorunludur.');
        return;
      }
    } else if (picked.length === 0) {
      setFormError('Eklenecek kalemleri işaretleyin.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await BillingService.saveDealerNotice({
          id: editing.id,
          dealerId: dealer.id,
          companyId: company.id,
          branchId: branchId || null,
          branchName: branchName || null,
          sourceKey: sourceKey || null,
          title: title.trim(),
          detail: detail.trim() || null,
          expiryDate: expiry,
          notifyEmail: email.trim(),
          notifyPhone: phone.trim() || null,
          notes: notes.trim() || null,
        });
        onSaved('Bildirim güncellendi.');
        return;
      }

      const selected = dueItems.filter((item) => picked.includes(item.sourceKey) && !item.openNoticeId);
      for (const item of selected) {
        await BillingService.saveDealerNotice({
          dealerId: dealer.id,
          companyId: company.id,
          branchId: item.branchId || null,
          branchName: item.branchName || null,
          sourceKey: item.sourceKey,
          title: item.title,
          detail: item.detail || null,
          expiryDate: dateOnly(item.expiryDate),
          notifyEmail: email.trim(),
          notifyPhone: phone.trim() || null,
          notes: notes.trim() || null,
        });
      }
      onSaved(selected.length === 1
        ? 'Bildirim açıldı. Eşik geldiyse hatırlatma maili gider.'
        : `${selected.length} bildirim açıldı. Eşik gelenlere hatırlatma maili gider.`);
    } catch (e: any) {
      const message = e?.message || 'Kayıt açılamadı.';
      setFormError(message);
      onError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? 'Bayi bildirimini düzenle' : 'Yeni bayi bildirimi'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        {formError && <Alert severity="error">{formError}</Alert>}
        <Autocomplete
          options={dealers}
          value={dealer}
          disabled={!!editing}
          getOptionLabel={(d) => d.name}
          onChange={(_, value) => {
            setDealer(value);
            setCompany(null);
            setPicked([]);
            setSourceKey('');
            setEmail(value?.email || '');
            setPhone(value?.phone || '');
          }}
          renderInput={(params) => <TextField {...params} label="Bayi" required />}
        />
        <Autocomplete
          options={dealerCompanies}
          value={company}
          disabled={!!editing || !dealer}
          getOptionLabel={(c) => c.name}
          onChange={(_, value) => {
            setCompany(value);
            setPicked([]);
            setSourceKey('');
            setBranchId('');
            setBranchName('');
          }}
          renderInput={(params) => <TextField {...params} label="Şirket" required />}
        />
        {!editing && company && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="subtitle2">Kalemler</Typography>
              {openItems.length > 0 && (
                <Button
                  size="small"
                  onClick={() => setPicked(picked.length === openItems.length ? [] : openItems.map((item) => item.sourceKey))}
                  sx={{ textTransform: 'none' }}
                >
                  {picked.length === openItems.length ? 'Seçimi kaldır' : 'Tümünü seç'}
                </Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {dueLoading
                ? 'Şirketin sözleşme, ürün, demo ve yazarkasaları yükleniyor…'
                : 'Bitiş tarihi olan kalemler şirketten gelir. İşaretledikleriniz ayrı bildirim olarak eklenir.'}
            </Typography>
            {dueError && <Alert severity="error" sx={{ mt: 1 }}>{dueError}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              {dueItems.map((item) => {
                const taken = !!item.openNoticeId;
                const where = [item.branchName, item.detail].filter(Boolean).join(' · ');
                return (
                  <FormControlLabel
                    key={item.sourceKey}
                    disabled={taken}
                    sx={{
                      m: 0,
                      px: 1,
                      py: 0.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      alignItems: 'flex-start',
                      width: '100%',
                      '& .MuiFormControlLabel-label': { flex: 1 },
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={taken || picked.includes(item.sourceKey)}
                        onChange={() => toggleItem(item.sourceKey)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, width: '100%', pt: 0.25 }}>
                        <Box>
                          <Typography variant="body2">{item.title}</Typography>
                          {where ? (
                            <Typography variant="caption" color="text.secondary">{where}</Typography>
                          ) : null}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {taken ? 'Zaten açık' : `${fmtDate(item.expiryDate)} · ${daysLabel(item.daysRemaining)}`}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
              {!dueLoading && !dueError && dueItems.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Bu şirkette bitiş tarihi olan sözleşme, ürün, demo veya yazarkasa yok.
                </Typography>
              )}
            </Box>
          </Box>
        )}
        {editing && (
          <>
            <TextField label="Kalem" value={title} onChange={(e) => setTitle(e.target.value)} required />
            <TextField label="Ayrıntı" value={detail} onChange={(e) => setDetail(e.target.value)} />
            <TextField
              label="Bitiş tarihi"
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
            {(branchName || branchId) && (
              <TextField label="Şube" value={branchName || branchId} disabled />
            )}
          </>
        )}
        <TextField
          label="Bayi e-postası"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          helperText="Hatırlatma bu adrese gider."
        />
        <TextField
          label="Bayi telefonu"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          helperText="Doluysa aynı hatırlatma SMS olarak da gider."
        />
        <TextField label="Not" value={notes} onChange={(e) => setNotes(e.target.value)} multiline minRows={2} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Vazgeç</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DealerNoticesTab;
